// =============================
// Imports
// =============================

// External Dependencies
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NextSeo } from 'next-seo';
import _get from 'lodash/get';

// Config
import { withTranslation } from '../../config/i18n';

// Actions
import storeWrapper from '../../store';
import {
  getAlbum,
  getAlbumTracks,
  resetAlbumAndItsTracks as resetAlbumAndItsTracksBase,
} from '../../store/actions/EntitiesActions';
import { setAlbumOpen } from '../../store/actions/ModalsActions';

// Components
import PrivateRoute from '../../components/containers/privateRoute';
import Album from '../../components/layouts/album';

// Helpers
import { getAlbumTitle, getAlbumCoverUrl } from '../../helpers/entity/album';
import { encodeAndRemovePercent, getWebsiteTitle, promiseAllAndWaitTillFulfilled } from '../../helpers/misc';
import { getValueByLocale } from '../../helpers/i18n';
import * as pth from '../../helpers/proptypes';

// =============================
// Page
// =============================

class AlbumPage extends PureComponent {
  static propTypes = {
    album: pth.album.isRequired,
    albumId: PropTypes.string.isRequired,
    config: pth.config.isRequired,
    getTracks: PropTypes.func.isRequired,
    i18n: PropTypes.shape({
      language: PropTypes.string,
    }).isRequired,
    isHttps: PropTypes.bool.isRequired,
    resetAlbumAndItsTracks: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    xHost: PropTypes.string.isRequired,
  };

  static getInitialProps = storeWrapper.getInitialPageProps(store => async ({ query }) => {
    const [albumId] = query.albumId;

    await promiseAllAndWaitTillFulfilled([
      store.dispatch(getAlbum(albumId)),
      store.dispatch(getAlbumTracks(albumId)),
    ]);

    return {
      albumId,
      namespacesRequired: ['common', 'pages', 'components', 'errors'],
    };
  });

  componentWillUnmount() {
    const { resetAlbumAndItsTracks } = this.props;

    resetAlbumAndItsTracks();
  }

  render() {
    const {
      album,
      albumId,
      config,
      getTracks,
      i18n: { language },
      isHttps,
      t,
      xHost,
      ...restProps
    } = this.props;

    const pageUrl = `https://${xHost}/albums/${albumId}/${encodeAndRemovePercent(getAlbumTitle(album))}`;

    const pageTitle = getWebsiteTitle(config, getAlbumTitle(album));
    const pageDescription = getValueByLocale(_get(album, 'descriptions', []) || [], language);
    const pageImages = getAlbumCoverUrl(album, 'large')
      ? [
        {
          url: getAlbumCoverUrl(album, 'large'),
          width: 500,
          height: 500,
        },
      ]
      : [];

    return (
      <PrivateRoute>
        <NextSeo
          title={pageTitle}
          description={pageDescription}
          canonical={pageUrl}
          openGraph={{
            type: 'website',
            url: pageUrl,
            title: pageTitle,
            description: pageDescription,
            images: pageImages,
          }}
        />
        <Album
          config={config}
          album={album}
          getTracks={page => getTracks(albumId, page)}
          locale={language}
          t={t}
          {...restProps}
        />
      </PrivateRoute>
    );
  }
}

function mapStateToProps({ user, core, entities, options, config }) {
  return {
    album: entities.album.data,
    hasMoreTracks: entities.albumTracks.nbPages - (entities.albumTracks.page + 1) > 0,
    isHttps: core.serverContext.locationProtocol === 'https:',
    tags: options.data.tags_only,
    tracks: entities.albumTracks.data,
    xHost: core.serverContext.xHost,
    isLogged: user.isLogged,
    parentPage: config.menus.data.parentPages.album,
  };
}

export default compose(
  withTranslation('pages'),
  connect(mapStateToProps, {
    getTracks: getAlbumTracks,
    openAlbumDescModal: () => setAlbumOpen(true),
    resetAlbumAndItsTracks: resetAlbumAndItsTracksBase,
  }),
)(AlbumPage);
